import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useAppDispatch, useAppSelector } from '@/app/store/hooks'
import { authApi } from '@/platform/auth/api/authApi'
import { authActions } from '@/platform/auth/model/authSlice'
import { redirectToSso } from '@/platform/auth/session/sso'
import { clearSession } from '@/platform/auth/session/tokens'
import { flushCheckpoints } from '@/platform/lifecycle/checkpoints'
import { getWalletSummary } from '@/platform/wallet/api/walletApi'
import type { WalletSummary } from '@/platform/wallet/model/types'

export function useAppShell() {
  const dispatch = useAppDispatch()
  const authenticated = useAppSelector(state => state.auth.status === 'authenticated')
  const googleDriveConsentOpen = useAppSelector(state => state.googleDrive.consent.open)
  const [wallet, setWallet] = useState<WalletSummary | null>(null)
  const [walletLoading, setWalletLoading] = useState(false)
  const [depositOpen, setDepositOpen] = useState(false)
  const [logoutBusy, setLogoutBusy] = useState(false)
  const [walletRevision, setWalletRevision] = useState(0)

  const refreshWallet = useCallback(() => setWalletRevision(value => value + 1), [])
  const closeDeposit = useCallback(() => {
    setDepositOpen(false)
    refreshWallet()
  }, [refreshWallet])

  useEffect(() => {
    if (!authenticated) { setWallet(null); return }
    let active = true
    setWalletLoading(true)
    void getWalletSummary().then(value => { if (active) setWallet(value) }).catch(() => undefined)
      .finally(() => { if (active) setWalletLoading(false) })
    return () => { active = false }
  }, [authenticated, walletRevision])

  const logout = async () => {
    setLogoutBusy(true)
    try {
      if (!await flushCheckpoints('exit')) { toast.error('Chưa lưu được tiến độ. Vui lòng thử lại.'); return }
      await authApi.logout()
      clearSession()
      dispatch(authActions.anonymous())
      redirectToSso('/')
    } catch {
      toast.error('Chưa đăng xuất được. Vui lòng thử lại.')
    } finally { setLogoutBusy(false) }
  }

  return {
    authenticated, wallet, walletLoading, depositOpen, googleDriveConsentOpen, logoutBusy,
    openDeposit: () => setDepositOpen(true), closeDeposit, refreshWallet,
    login: () => redirectToSso(), logout,
  }
}
